import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { tokenMatch, tokenScore, normalizeText } from '@/lib/search-match';

export interface CarSuggestion {
  variantId: string;
  variantName: string;
  variantSlug: string;
  modelName: string;
  modelSlug: string;
  brandName: string;
  brandSlug: string;
  imageUrl: string | null;
  powertrainType: string;
  priceDkk: number | null;
  rangeWltpKm: number | null;
  availabilityStatus: string;
  expectedPriceDkk: number | null;
  expectedLaunchDate: string | null;
}

export interface BrandSuggestion {
  brandName: string;
  brandSlug: string;
  modelName?: string;
  modelSlug?: string;
}

interface UseCarSuggestionsOptions {
  query: string;
  enabled?: boolean;
  limit?: number;
}

export const POPULAR_BRANDS = ['Tesla', 'Volkswagen', 'BMW', 'Audi', 'Mercedes-Benz', 'Toyota', 'Volvo', 'Skoda', 'Kia', 'Hyundai'];

interface SuggestionResult {
  suggestions: CarSuggestion[];
  didYouMean: BrandSuggestion[];
}

async function fetchSuggestions(query: string, limit: number): Promise<SuggestionResult> {
  if (!query || query.length < 2) return { suggestions: [], didYouMean: [] };

  // 1) Hent alle aktive modeller m. brand
  const { data: matchingModels } = await supabase
    .from('models')
    .select(`id, name, slug, image_url, brands!inner (id, name, slug)`)
    .eq('is_active', true);

  const allModels = matchingModels || [];

  // 2) Token-baseret filtrering på brand+model
  const matched = allModels.filter((m: any) =>
    tokenMatch([m.brands.name, m.name], query)
  );

  let matchingModelIds = matched.map((m: any) => m.id);
  let didYouMean: BrandSuggestion[] = [];

  // Hvis ingen match: byg "mente du"-forslag baseret på partielle token-scores
  if (matchingModelIds.length === 0) {
    const scored = allModels
      .map((m: any) => ({
        model: m,
        score: tokenScore([m.brands.name, m.name], query),
      }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    didYouMean = scored.map((s) => ({
      brandName: s.model.brands.name,
      brandSlug: s.model.brands.slug,
      modelName: s.model.name,
      modelSlug: s.model.slug,
    }));
    return { suggestions: [], didYouMean };
  }

  // 3) Hent variants for matchede modeller
  const { data, error } = await supabase
    .from('variants')
    .select(`
      id, name, slug, powertrain_type, availability_status, expected_price_dkk, expected_launch_date,
      models!inner (id, name, slug, image_url, brands!inner (id, name, slug)),
      prices_dk (price_dkk, price_type),
      powertrain_specs (range_wltp_km)
    `)
    .eq('is_active', true)
    .in('model_id', matchingModelIds)
    .limit(limit * 3); // hent flere så vi kan re-filtrere på variant-niveau

  if (error) throw error;

  // 4) Yderligere variant-niveau filtrering: kræv stadig at alle tokens matcher
  // (brand + model + variant tilsammen). Det fanger "tesla model y performance".
  const filteredVariants = (data || []).filter((v: any) =>
    tokenMatch([v.models.brands.name, v.models.name, v.name], query)
  );

  // 4b) Modeller uden (aktive) varianter — fx kommende modeller — skal stadig kunne findes.
  // Vigtigt: afgør dette ud fra en separat, ikke-trunkeret forespørgsel, så modeller
  // med mange varianter ikke fejlagtigt markeres som "uden varianter".
  const { data: variantModelRows } = await supabase
    .from('variants')
    .select('model_id')
    .eq('is_active', true)
    .in('model_id', matchingModelIds);

  const modelIdsWithVariants = new Set((variantModelRows || []).map((v: any) => v.model_id));
  didYouMean = matched
    .filter((m: any) => !modelIdsWithVariants.has(m.id))
    .slice(0, 5)
    .map((m: any) => ({
      brandName: m.brands.name,
      brandSlug: m.brands.slug,
      modelName: m.name,
      modelSlug: m.slug,
    }));



  // 5) Sortér: eksakt brand-match først, derefter eksakt model-match
  const qNorm = normalizeText(query);
  filteredVariants.sort((a: any, b: any) => {
    const aBrand = normalizeText(a.models.brands.name);
    const bBrand = normalizeText(b.models.brands.name);
    const aModel = normalizeText(a.models.name);
    const bModel = normalizeText(b.models.name);

    const aBrandStarts = aBrand.startsWith(qNorm) || qNorm.startsWith(aBrand);
    const bBrandStarts = bBrand.startsWith(qNorm) || qNorm.startsWith(bBrand);
    if (aBrandStarts !== bBrandStarts) return aBrandStarts ? -1 : 1;

    const aModelExact = qNorm.includes(aModel);
    const bModelExact = qNorm.includes(bModel);
    if (aModelExact !== bModelExact) return aModelExact ? -1 : 1;

    return 0;
  });

  const suggestions: CarSuggestion[] = filteredVariants.slice(0, limit).map((variant: any) => {
    const listPrice = variant.prices_dk?.find((p: any) => p.price_type === 'List');
    const price = listPrice?.price_dkk || variant.prices_dk?.[0]?.price_dkk || null;

    return {
      variantId: variant.id,
      variantName: variant.name,
      variantSlug: variant.slug,
      modelName: variant.models.name,
      modelSlug: variant.models.slug,
      brandName: variant.models.brands.name,
      brandSlug: variant.models.brands.slug,
      imageUrl: variant.models.image_url,
      powertrainType: variant.powertrain_type,
      priceDkk: price,
      rangeWltpKm: variant.powertrain_specs?.range_wltp_km || null,
      availabilityStatus: variant.availability_status,
      expectedPriceDkk: variant.expected_price_dkk ?? null,
      expectedLaunchDate: variant.expected_launch_date ?? null,
    };
  });

  return { suggestions, didYouMean };
}

export function useCarSuggestions({ query, enabled = true, limit = 10 }: UseCarSuggestionsOptions) {
  return useQuery({
    queryKey: ['car-suggestions-v2', query],
    queryFn: () => fetchSuggestions(query, limit),
    enabled: enabled && query.length >= 2,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function usePrefetchPopularBrands() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      POPULAR_BRANDS.forEach((brand) => {
        queryClient.prefetchQuery({
          queryKey: ['car-suggestions-v2', brand],
          queryFn: () => fetchSuggestions(brand, 10),
          staleTime: 1000 * 60 * 5,
        });
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [queryClient]);
}

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
